// Phaser implementation of the Renderer interface (IMPLEMENTATION §2.9).
//   factory mode (default): BootScene bakes the atlas, FactoryScene draws the board from the sim.
//   spike mode (?spike=1):  the M0 SpikeScene (7×9 checker + highlight) for the capability probe only; the
//                           sim still runs but is not drawn.
// `spike()` runs the M0 probe on whichever scene is up; while it runs it owns the camera (M1 decision 14).
import type { Layout } from '../../layout/computeLayout.ts';
import type { ViewportInfo } from '../../platform/viewport.ts';
import type { SimEvent } from '../../sim/events.ts';
import { viewCells, worldToClient } from '../cameraMath.ts';
import type { FxConfig } from '../fxConfig.ts';
import type { JuiceCfg } from '../juice/config.ts';
import type { GeoCfg } from '../juice/geometry.ts';
import type { ShotGate } from '../juice/shotGate.ts';
import type { RenderDebug, Renderer, RenderSource, SpikeReport } from '../Renderer.ts';
import { type BakeInput, type BootResult, createBootScene } from './BootScene.ts';
import { BoardCamera } from './camera.ts';
import { createGame } from './createGame.ts';
import { type BoardFxStats, createFactoryScene, type FxMotion } from './FactoryScene.ts';
import type { FrameIndex } from './frames.ts';
import {
  backingSize,
  destroyGame,
  gameCanvas,
  type PGame,
  type PScene,
  resizeCamera,
  resizeGame,
  setFpsLimit,
  snapshotPixel,
} from './phaserCompat.ts';
import { createSpikeScene } from './SpikeScene.ts';
import { runSpikeProbe } from './spikeProbe.ts';

export interface PhaserRendererOptions {
  parent: HTMLElement;
  background: string;
  cellWorldUnits: number;
  frameCss: number;
  /** false = no `render.mipmapFilter` (`?mipmaps=off`, the spike's negative control). */
  mipmaps: boolean;
  /** 'spike' boots the M0 SpikeScene (?spike=1) instead of Boot + Factory. */
  mode: 'factory' | 'spike';
  fx: FxConfig;
  /** M10 board FX: the juice table, overlay geometry, motion settings and the FX clock's largest step. */
  juice: JuiceCfg;
  geo: GeoCfg;
  motion: FxMotion;
  maxStepMs: number;
  /** The visible-shot decision shared with the DOM FX (one decision per Gallery shot). */
  shots?: ShotGate;
  /** The catalog, bake size and generator (read when the BootScene runs). */
  bake(): BakeInput;
  /** Frame syncs kept for `debug.syncMs()`. */
  syncSamples: number;
}

export interface PhaserRenderer extends Renderer {
  /** Atlas bake result (null before BootScene ran or in spike mode). */
  atlas(): BootResult | null;
  /** M10: the board FX counters (`__gm.render.fx()`); null in spike mode. */
  fxStats(): BoardFxStats | null;
  /** M10: resets the FX peak counters. */
  fxReset(): void;
}

function nextFrame(): Promise<void> {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

export function createPhaserRenderer(o: PhaserRendererOptions): PhaserRenderer {
  const spikeMode = o.mode === 'spike';
  let onFrame: ((now: number) => void) | null = null;
  let src: RenderSource | null = null;
  let frames: FrameIndex | null = null;
  let boot: BootResult | null = null;
  const camera = new BoardCamera(o.cellWorldUnits, o.fx.easeOut);
  const spike = createSpikeScene(o.cellWorldUnits, o.frameCss, (now) => onFrame?.(now));
  const factory = createFactoryScene({
    cellWu: o.cellWorldUnits,
    fx: o.fx,
    juice: o.juice,
    geo: o.geo,
    motion: o.motion,
    maxStepMs: o.maxStepMs,
    ...(o.shots ? { shots: o.shots } : {}),
    muzzle: (frame) => boot?.muzzles.get(frame) ?? null,
    camera,
    frames: () => {
      if (!frames) throw new Error('FactoryScene before BootScene');
      return frames;
    },
    source: () => src,
    onFrame: (now) => onFrame?.(now),
    onReady: () => undefined,
  });
  const bootScene = createBootScene(o.bake, factory.hooks.key, (r) => {
    boot = r;
    frames = r.index;
  });
  let game: PGame | null = null;
  let current: { layout: Layout; vp: ViewportInfo } | null = null;
  let fpsLimit = 0;
  let probe: Promise<SpikeReport> | null = null;
  /** While the `?spike=1` probe runs it owns the camera (identity, backing-store px): board fits wait. */
  let probing = false;
  const syncs: number[] = [];
  let frameCount = 0;
  let idleWaiters: Array<() => void> = [];
  let quiet = 0;
  let received = 0;
  /** Resolves `idle()` waiters after two quiet frames. */
  const settle = (busy: boolean): void => {
    if (idleWaiters.length === 0) return;
    quiet = busy ? 0 : quiet + 1;
    if (quiet >= 2) {
      const w = idleWaiters;
      idleWaiters = [];
      quiet = 0;
      for (const r of w) r();
    }
  };

  const scene = (): PScene | null => (spikeMode ? spike.scene() : factory.scene());

  const apply = (animateMs: number): void => {
    if (!game || !current) return;
    const { layout, vp } = current;
    resizeGame(game, vp.iw, vp.ih, vp.dprEff);
    const sc = scene();
    if (sc) {
      const { w, h } = backingSize(vp.iw, vp.ih, vp.dprEff);
      resizeCamera(sc, w, h);
    }
    if (probing) return;
    factory.setCellCss(layout.cellCss);
    factory.setView(viewCells(layout, vp.iw, vp.ih));
    if (spikeMode) spike.fit(layout, vp.dprEff);
    else {
      camera.fit(layout, vp.dprEff, animateMs, performance.now());
      const fs = factory.scene();
      if (fs) camera.step(fs, performance.now());
    }
  };

  const debug: RenderDebug = {
    count: (kind) => (spikeMode ? 0 : factory.count(kind)),
    debugPos(id) {
      const w = spikeMode ? null : factory.worldPos(id);
      const fit = camera.current();
      if (!w || !fit || !current) return null;
      return worldToClient(fit, current.vp.dprEff, w.x, w.y);
    },
    freezeAt(ms) {
      // M10: the FX clock stops `ms` after the next frame (events queued in this task start at that frame)
      if (!spikeMode) factory.freezeAt(ms);
    },
    idle() {
      return new Promise<void>((resolve) => {
        idleWaiters.push(resolve);
      }).then(async () => {
        await nextFrame();
      });
    },
    syncMs: () => [...syncs],
    frames: () => frameCount,
    eventsReceived: () => received,
  };

  return {
    kind: 'phaser',
    debug,
    atlas: () => boot,
    fxStats: () => (spikeMode ? null : factory.fxStats()),
    fxReset: () => factory.fxReset(),
    bind(s) {
      src = s;
      factory.rebuild();
    },
    async init(layout, vp) {
      current = { layout, vp };
      game = await createGame({
        parent: o.parent,
        vp,
        background: o.background,
        mipmapFilter: o.mipmaps ? 'LINEAR_MIPMAP_LINEAR' : null,
        scenes: spikeMode ? [spike.hooks] : [bootScene, factory.hooks],
      });
      if (fpsLimit > 0) setFpsLimit(game, fpsLimit);
      apply(0);
    },
    relayout(layout, vp, animate) {
      current = { layout, vp };
      // reduce motion (UX §5 ExpansionDone "150 ms crossfade"): the camera snaps, the crumble fades
      apply(animate === true && !o.motion.reduceMotion() ? o.fx.expansionTweenMs : 0);
    },
    drive(cb) {
      onFrame = cb;
    },
    frame(alpha) {
      if (spikeMode || probing) {
        // the spike scene has no views: it is idle whenever the probe is not running
        settle(probing);
        return;
      }
      frameCount += 1;
      const t0 = performance.now();
      factory.frame(alpha, t0);
      const ms = performance.now() - t0;
      syncs.push(ms);
      if (syncs.length > o.syncSamples) syncs.shift();
      settle(factory.busy());
    },
    onEvents(evs: readonly SimEvent[]) {
      received += evs.length;
      if (!spikeMode) factory.queue(evs);
    },
    rebuildFromState() {
      factory.rebuild();
    },
    setHighlight(cell) {
      if (spikeMode) spike.highlight(cell);
      else factory.highlight(cell);
    },
    setInputOverlay(o) {
      if (!spikeMode) factory.overlay(o);
    },
    highlighted: () => (spikeMode ? spike.highlighted() : factory.highlighted()),
    pixelAt(clientX, clientY) {
      if (!game || !current) return Promise.resolve(null);
      const d = current.vp.dprEff;
      return snapshotPixel(game, clientX * d, clientY * d);
    },
    spike() {
      if (!probe) {
        const g = game;
        const sc = scene();
        const cur = current;
        if (!g || !sc || !cur) return Promise.reject(new Error('renderer not initialised'));
        probing = true;
        probe = runSpikeProbe(g, sc, cur.vp, () => {
          probing = false;
          apply(0);
        });
      }
      return probe;
    },
    canvas() {
      return game ? gameCanvas(game) : null;
    },
    setFpsLimit(fps) {
      fpsLimit = fps;
      if (game) setFpsLimit(game, fps);
    },
    destroy() {
      if (game) destroyGame(game);
      game = null;
      onFrame = null;
    },
  };
}
